// ── Shared ocean swell pattern ───────────────────────────────────────────────
// Single source of truth for the two-sine swell used both by the surface vertex
// displacement (below) and by the underwater post-process tint (PostProcess.ts),
// so the tint boundary rides the exact same phase/direction as the real water.
// Returns the raw (un-amplified) height in ~[-(1+cross), (1+cross)] and writes
// the analytic horizontal gradient of that raw height into `grad`.
export const oceanWavePatternGLSL = /* glsl */`
    float oceanWavePattern(vec2 xz, float t, vec2 dir1, vec2 dir2, float k, float crossW, out vec2 grad) {
        float phase1 = dot(xz, dir1) * k + t;
        float phase2 = dot(xz, dir2) * (k * 1.3) - t * 0.85;
        grad = dir1 * (k * cos(phase1))
             + dir2 * (k * 1.3 * crossW * cos(phase2));
        return sin(phase1) + crossW * sin(phase2);
    }
`;

export const surfaceVertex =
/*glsl*/`
    #include <ocean>
` + oceanWavePatternGLSL + /*glsl*/`

    uniform vec3  _CameraForward;
    uniform float _SurfaceWaveAmplitude;
    uniform float _SurfaceWaveLength;
    uniform float _SurfaceWaveSpeed;
    uniform float _SurfaceWaveRange;
    uniform float _SurfaceWaveForwardBias;
    uniform float _SurfaceWaveSteepness;

    varying vec2 _worldPos;
    varying vec2 _uv;
    varying float _elevation;
    varying vec3 _waveNormal;

    void main()
    {
        vec4 worldPos = modelMatrix * vec4(position, 1.0);

        // ── Near-camera vertex displacement ──────────────────────────────────
        // Only the strip of ocean in front of the camera is bumped; amplitude
        // fades to 0 with distance + behind the camera, so the swell fuses
        // seamlessly with the rest of the flat surface.
        vec2 toCam = worldPos.xz - cameraPosition.xz;
        float camDist = length(toCam);
        float distMask = 1.0 - smoothstep(0.0, _SurfaceWaveRange, camDist);

        // Forward bias: restrict bumps to what's ahead of the camera's heading.
        vec2 fwd = normalize(_CameraForward.xz + vec2(1e-5));
        float facing = dot(normalize(toCam + vec2(1e-5)), fwd);
        float fwdMask = mix(1.0, smoothstep(-0.15, 0.55, facing), _SurfaceWaveForwardBias);

        float mask = distMask * fwdMask;

        float elevation = 0.0;
        _waveNormal = vec3(0.0);

        if (mask > 0.001 && _SurfaceWaveAmplitude > 0.0) {
            float k = 6.2831853 / max(_SurfaceWaveLength, 0.01);
            vec2 dir1 = normalize(_WaveVelocity1 + vec2(1e-5));
            vec2 dir2 = normalize(_WaveVelocity2 + vec2(1e-5));
            float t = _Time * _SurfaceWaveSpeed;
            float crossWave = _SurfaceWaveSteepness * 0.6;

            vec2 grad;
            float h = oceanWavePattern(worldPos.xz, t, dir1, dir2, k, crossWave, grad);
            float amp = _SurfaceWaveAmplitude * mask;
            elevation = h * amp;

            // Analytic gradient → tangent-space normal offset. The fragment
            // builds normals in a z-up space then swizzles .xzy to world, so
            // (-dH/dx, -dH/dz, 0) maps to the correct world tilt.
            grad *= amp;
            _waveNormal = vec3(-grad.x, -grad.y, 0.0);
        }

        worldPos.y += elevation;

        _worldPos = worldPos.xz;
        _uv = _worldPos * _NormalMapScale;
        _elevation = worldPos.y;
        gl_Position = projectionMatrix * viewMatrix * worldPos;
    }
`;

export const surfaceFragment = 
/*glsl*/`
    #include <ocean>

    uniform float _EdgeFadeDistance;
    uniform float _HorizonFadeStart; // eye distance where the surface starts blending to horizon color
    uniform float _HorizonFadeEnd;   // eye distance where the surface fully matches the horizon color
    uniform float _SurfaceWaveRange; // radius (world units) of the near-camera swell patch discard circle

    // Foam mask — top-down island silhouette rendered at runtime
    uniform sampler2D _FoamMask;
    uniform vec2 _FoamMaskCenter;   // World XZ center of the mask region (auto-baked from island)
    uniform vec2 _FoamCenterOffset; // User-controllable XZ nudge on top of baked center
    uniform vec2 _FoamMaskSize;     // World XZ extent of the mask region
    uniform float _FoamWidth;
    uniform float _FoamIntensity;
    uniform float _FoamAnimSpeed;
    uniform float _FoamEdgeNoiseAmt;
    uniform float _FoamWobbleAmt;
    uniform float _FoamWobbleFreq;
    uniform float _FoamWobbleSpeed;
    uniform float _FoamRadius;
    uniform float _FoamLineFrequency;
    uniform float _FoamLineThickness;
    uniform float _FoamLineCount;
    uniform float _FoamLineBreakup;
    uniform vec3 _FoamLineColor;
    
    uniform float _Ripples[15];  // MAX_RIPPLES * 3 (x, z, time)
    uniform int _RippleCount;
    uniform float _RippleSpeed;
    uniform float _RippleLifetime;
    uniform float _RippleWidth;
    uniform float _RippleNormalStrength;  // normal perturbation amplitude

    uniform float _ReflectionFresnelPower;  // Fresnel exponent — lower = reflective at more angles
    uniform float _ReflectionFloor;         // minimum reflectivity at any view angle
    uniform float _SkyReflBrightness;       // scales the analytical skybox reflection (0–1)
    uniform float _SkyReflFalloff;          // exponent on fresnelBase for sky only — sharpens near→far gradient
    uniform vec3  _SurfaceColor;            // RGB tint (1,1,1 = no tint)
    uniform float _SurfaceOpacity;          // 0 = physics-Fresnel alpha, 1 = fully opaque
    uniform sampler2D _SceneColor;
    uniform vec2 _SceneResolution;
    uniform float _WaterBlurStrength;
    uniform float _WaterBlurRadius;
    uniform float _WaterBlurOpacity;
    uniform float _WaterlineCompositeOpacity;

    // Depth-intersection foam — opaque scene depth captured pre-ocean by SceneDepth.ts.
    // highp: the sampled depth lives near 1.0, where mediump (the iOS default for
    // samplers) quantizes catastrophically and makes the contact line flicker.
    uniform highp sampler2D _SceneDepth;
    uniform float _CameraNear;
    uniform float _CameraFar;
    uniform float _EdgeFoamWidth;
    uniform float _EdgeFoamIntensity;
    uniform float _EdgeFoamUnderwaterMul;  // dim factor for camera-below-water (refractive sheen vs surface foam)
    uniform vec3  _EdgeFoamColor;
    uniform float _EdgeFoamFadeStartZ;  // world Z at/above which foam is full strength
    uniform float _EdgeFoamFadeEndZ;    // world Z (further back) at/below which foam is gone

    varying vec2 _worldPos;
    varying vec2 _uv;
    varying float _elevation;
    varying vec3 _waveNormal;  // tangent-space tilt from near-camera vertex displacement

    float calcEdgeFade(vec2 pos) {
        float distFromNearEdge = -pos.y;
        
        return smoothstep(0.0, _EdgeFadeDistance, distFromNearEdge);
    }
    
    // --- Hash-based noise (no texture) for foam ---
    float foamHash(vec2 p) {
        vec3 p3 = fract(vec3(p.xyx) * 0.1031);
        p3 += dot(p3, p3.yzx + 33.33);
        return fract((p3.x + p3.y) * p3.z);
    }
    float foamNoise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        f = f * f * (3.0 - 2.0 * f); // smoothstep
        float a = foamHash(i);
        float b = foamHash(i + vec2(1.0, 0.0));
        float c = foamHash(i + vec2(0.0, 1.0));
        float d = foamHash(i + vec2(1.0, 1.0));
        return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
    }
    // Quality-adaptive FBM (controlled by _FoamQuality uniform)
    // 1 = single noise tap (fastest), 2 = 2 octaves, 3 = full 3-octave FBM (default)
    uniform int _FoamQuality;
    float foamFbm(vec2 p) {
        if (_FoamQuality <= 1) return foamNoise(p);
        vec2 q = p;
        float v = 0.50 * foamNoise(q); q *= 2.01;
        v += 0.30 * foamNoise(q);
        if (_FoamQuality <= 2) return v / 0.80;
        q *= 2.03;
        v += 0.20 * foamNoise(q);
        return v;
    }

    float calcFoam(vec2 pos) {
        // Wobble: displace the lookup position with animated noise
        // This makes the foam line itself undulate organically
        float wt = _Time * _FoamWobbleSpeed;
        vec2 wobble = vec2(
            foamFbm(pos * _FoamWobbleFreq + vec2(wt, 0.0)) - 0.5,
            foamFbm(pos * _FoamWobbleFreq + vec2(0.0, wt + 17.3)) - 0.5
        ) * _FoamWobbleAmt;
        vec2 wobbledPos = pos + wobble;

        // Convert world XZ to mask UV (0-1 range within the captured region)
        // _FoamCenterOffset lets you nudge the ring without regenerating the mask.
        vec2 maskUV = (wobbledPos - (_FoamMaskCenter + _FoamCenterOffset)) / _FoamMaskSize + 0.5;
        // The ortho camera's right axis = world -X, so flip U to match
        maskUV.x = 1.0 - maskUV.x;

        // Scale UV around center to push foam ring inward (<1) or outward (>1)
        maskUV = (maskUV - 0.5) / _FoamRadius + 0.5;
        
        // Outside the mask region — no foam
        if (maskUV.x < 0.0 || maskUV.x > 1.0 || maskUV.y < 0.0 || maskUV.y > 1.0) {
            return 0.0;
        }
        
        float sdf = texture2D(_FoamMask, maskUV).r;
        float signedDist = (sdf - 0.5) * 3.0;
        float waterDist = max(0.0, -signedDist);
        float waterSide = 1.0 - smoothstep(0.0, _FoamWidth, signedDist);

        float mainLine = 1.0 - smoothstep(0.0, _FoamLineThickness, abs(signedDist));
        mainLine *= waterSide;

        float t = _Time * _FoamAnimSpeed;
        float waveCoord = waterDist * _FoamLineFrequency - t;
        float stripePhase = abs(fract(waveCoord) - 0.5) * 2.0;
        float stripes = 1.0 - smoothstep(0.0, _FoamLineThickness * _FoamLineFrequency, stripePhase);
        float lineLimit = 1.0 - smoothstep(_FoamWidth * max(_FoamLineCount, 1.0), _FoamWidth * (max(_FoamLineCount, 1.0) + 0.75), waterDist);
        stripes *= lineLimit;

        float breakup = foamFbm(pos * 7.5 + vec2(t * 0.23, -t * 0.17));
        float keep = smoothstep(_FoamLineBreakup, 1.0, breakup);
        stripes *= mix(1.0, keep, _FoamLineBreakup);

        float shimmer = foamFbm(pos * 11.0 + t * 0.3);
        float foam = max(mainLine, stripes);
        foam *= 0.82 + shimmer * 0.28 + _FoamEdgeNoiseAmt * (shimmer - 0.5);

        return clamp(foam, 0.0, 1.0) * _FoamIntensity;
    }

    // Linearize a [0,1] depth-buffer value to positive view-space distance.
    // The naive form (near*far)/(far - depth*(far-near)) subtracts two large
    // near-equal numbers in the denominator for distant fragments (depth≈1),
    // which loses almost all precision in fragment mediump — the case on iOS,
    // where it makes the back-rock contact line jitter ±0.1u between frames.
    // Algebraically identical refactor that keeps the small term small:
    //   far - depth*(far-near)  ==  far*(1.0-depth) + depth*near
    float linearizeDepthBuffer(float depth, float near, float far) {
        return (near * far) / (far * (1.0 - depth) + depth * near);
    }

    // Industry-standard intersection foam: where the ocean fragment is close in
    // view-space to an opaque scene fragment, brighten with foam. Works
    // regardless of object shape/slope/geometry density because it lives on
    // the (wave-displaced) ocean surface and reads the actual scene depth.
    float calcEdgeFoam(vec2 screenUv, float worldZ) {
        float sceneDepth = texture2D(_SceneDepth, screenUv).x;
        // Skybox / cleared background reads as 1.0 — no opaque object here.
        if (sceneDepth >= 0.9999) return 0.0;
        float sceneLinear = linearizeDepthBuffer(sceneDepth, _CameraNear, _CameraFar);
        // Ocean eye-depth analytically, NOT from gl_FragCoord.z. The depth-buffer
        // value is mediump/16-bit near the contact line on mobile, so deriving
        // oceanLinear from it injected per-frame jitter into depthDiff exactly
        // where foam peaks (depthDiff≈0) → the flicker. The ocean fragment's world
        // position is already known in full precision via the varyings, so its
        // view-space Z (perpendicular eye depth, same space as sceneLinear) is
        // exact and stable. _worldPos = worldPos.xz, _elevation = worldPos.y.
        float oceanLinear = -(viewMatrix * vec4(_worldPos.x, _elevation, _worldPos.y, 1.0)).z;
        float depthDiff = sceneLinear - oceanLinear;

        if (depthDiff <= 0.0) return 0.0;
        float foam = 1.0 - smoothstep(0.0, _EdgeFoamWidth, depthDiff);
        foam *= smoothstep(_EdgeFoamFadeEndZ, _EdgeFoamFadeStartZ, worldZ);

        return foam;
    }

    vec3 sampleBlurredScene(vec2 screenUv, vec3 normal) {
        vec2 texel = 1.0 / max(_SceneResolution, vec2(1.0));
        vec2 normalOffset = normal.xz * _WaterBlurStrength;
        vec2 radius = texel * _WaterBlurRadius;
        vec2 uv = clamp(screenUv + normalOffset, vec2(0.001), vec2(0.999));

        vec3 color = texture2D(_SceneColor, uv).rgb * 0.24;
        color += texture2D(_SceneColor, clamp(uv + vec2( radius.x, 0.0), vec2(0.001), vec2(0.999))).rgb * 0.12;
        color += texture2D(_SceneColor, clamp(uv + vec2(-radius.x, 0.0), vec2(0.001), vec2(0.999))).rgb * 0.12;
        color += texture2D(_SceneColor, clamp(uv + vec2(0.0,  radius.y), vec2(0.001), vec2(0.999))).rgb * 0.12;
        color += texture2D(_SceneColor, clamp(uv + vec2(0.0, -radius.y), vec2(0.001), vec2(0.999))).rgb * 0.12;
        color += texture2D(_SceneColor, clamp(uv + radius, vec2(0.001), vec2(0.999))).rgb * 0.07;
        color += texture2D(_SceneColor, clamp(uv - radius, vec2(0.001), vec2(0.999))).rgb * 0.07;
        color += texture2D(_SceneColor, clamp(uv + vec2(radius.x, -radius.y), vec2(0.001), vec2(0.999))).rgb * 0.07;
        color += texture2D(_SceneColor, clamp(uv + vec2(-radius.x, radius.y), vec2(0.001), vec2(0.999))).rgb * 0.07;
        return color;
    }
    
    // Calculate ripple normal perturbation and subtle foam
    vec3 calcRippleNormal(vec2 worldPos, out float rippleFoam) {
        vec3 normalOffset = vec3(0.0);
        rippleFoam = 0.0;
        
        for (int i = 0; i < 3; i++) {  // MAX_RIPPLES - reduced for mobile performance
            if (i >= _RippleCount) break;
            
            float rippleX = _Ripples[i * 3 + 0];
            float rippleZ = _Ripples[i * 3 + 1];
            float rippleTime = _Ripples[i * 3 + 2];
            
            if (rippleTime < 0.0) continue;  // Inactive ripple
            
            vec2 rippleCenter = vec2(rippleX, rippleZ);
            vec2 toCenter = worldPos - rippleCenter;
            float dist = length(toCenter);
            float radius = rippleTime * _RippleSpeed;
            
            // Wave pattern - circular rings
            float wave = abs(dist - radius);
            float mask = smoothstep(_RippleWidth, 0.0, wave);
            float fade = 1.0 - (rippleTime / _RippleLifetime);
            fade = fade * fade;
            
            // Create radial normal distortion — strength matches ocean wave normals
            vec2 direction = normalize(toCenter);
            float angle = (dist - radius) * 15.0;  // Wave frequency
            float waveHeight = sin(angle) * mask * fade;

            // Normal perturbation: use _RippleNormalStrength so it is consistent
            // with the normal-map wave normals and produces matching reflections.
            normalOffset.x += direction.x * waveHeight * _RippleNormalStrength;
            normalOffset.z += direction.y * waveHeight * _RippleNormalStrength;
            
            // Very subtle foam on wave crests (not pure white)
            float crest = smoothstep(-0.3, 0.3, sin(angle)) * mask * fade;
            rippleFoam += crest * 0.15;  // Very subtle, just 15% foam
        }
        
        rippleFoam = clamp(rippleFoam, 0.0, 1.0);
        return normalOffset;
    }

    void main()
    {
        // Near-camera wave patch: complementary circular discard. The main
        // surface discards INSIDE the circle (leaves a hole); the patch mesh
        // (identical shader, OCEAN_PATCH defined) discards OUTSIDE it and
        // fills that hole with much finer tessellation so the swell's
        // silhouette isn't faceted. Both sides use the same _SurfaceWaveRange
        // radius and cameraPosition — displacement is already 0 there
        // (distMask hits exactly 0 at _SurfaceWaveRange in the vertex
        // shader), so the seam is watertight regardless of tessellation, and
        // the two semi-transparent meshes never double-blend the same pixel.
        vec2 toCam = _worldPos - cameraPosition.xz;
        float distSq = dot(toCam, toCam);
        float radiusSq = _SurfaceWaveRange * _SurfaceWaveRange;
        #ifdef OCEAN_PATCH
            if (distSq > radiusSq) discard;
        #else
            if (distSq <= radiusSq) discard;
        #endif

        float edgeFade = calcEdgeFade(_worldPos);
        if (edgeFade <= 0.0) discard;

        float foam = calcFoam(_worldPos);
        
        // Add ripple normal perturbation with subtle foam
        float rippleFoam = 0.0;
        vec3 rippleNormalOffset = calcRippleNormal(_worldPos, rippleFoam);
        foam = clamp(foam + rippleFoam, 0.0, 1.0);

        vec3 viewVec = vec3(_worldPos.x, _elevation, _worldPos.y) - cameraPosition;
        float viewLen = length(viewVec);
        vec3 viewDir = viewVec / viewLen;

        vec3 normal = texture2D(_NormalMap1, _uv + _WaveVelocity1 * _Time).xyz * 2.0 - 1.0;
        normal += texture2D(_NormalMap2, _uv + _WaveVelocity2 * _Time).xyz * 2.0 - 1.0;
        normal *= _NormalMapStrength;
        normal += vec3(0.0, 0.0, 1.0);
        normal += rippleNormalOffset;  // Add ripple normal perturbation
        normal += _waveNormal;         // Add near-camera vertex-displacement tilt
        normal = normalize(normal).xzy;

        sampleDither(gl_FragCoord.xy);

        // Depth-intersection foam computed once and shared by both above-water
        // and underwater paths. Underwater it reads as a dim silvery sheen — the
        // physical effect when looking up at the surface meeting a rock, where
        // refraction concentrates light at the boundary.
        vec2 screenUv = gl_FragCoord.xy / max(_SceneResolution, vec2(1.0));
        // _worldPos.y is the ocean fragment's world Z (varying = worldPos.xz).
        float edgeFoamRaw = calcEdgeFoam(screenUv, _worldPos.y);

        if (cameraPosition.y > _elevation)
        {
            // Horizon haze driven by the view's grazing angle, NOT distance. The
            // ocean-plane edge and any grazing-reflection speck always sit where the
            // line of sight is near-horizontal (-viewDir.y -> 0), regardless of how
            // far the horizon is for the current camera height. Veil that band by
            // blending the surface to the sky's horizon color + flattening its
            // normal, so the plane edge dissolves into the sky. 1 at the horizon,
            // 0 once looking down past _HorizonFadeEnd radians-ish below horizontal.
            // Scoped to the above-water branch only: viewDir always points down-ish
            // here (-viewDir.y >= 0). Computing this before the branch split used to
            // clamp negative -viewDir.y (upward rays, i.e. looking up from
            // underwater) to 0 via max(0.0, ...) — the same value as "at the
            // horizon" — which forced full haze (and a dead-flat normal) across the
            // entire underwater upward view, wiping out the visible wave normal.
            float horizonHaze = 1.0 - smoothstep(_HorizonFadeStart, _HorizonFadeEnd, max(0.0, -viewDir.y));
            normal = normalize(mix(normal, vec3(0.0, 1.0, 0.0), horizonHaze));

            // fresnelBase: raw Fresnel curve — approaches 0 directly overhead.
            // reflectivity: floored version used for body-color blending only.
            float fresnelBase = pow(1.0 - max(0.0, dot(-viewDir, normal)), _ReflectionFresnelPower);
            float reflectivity = max(fresnelBase, _ReflectionFloor);

            // Sky-only reflection: analytical skybox sample via reflected view direction
            vec3 skyRefl = sampleSkybox(reflect(viewDir, normal)) * _SkyReflBrightness;
            vec3 surface = pow(fresnelBase, _SkyReflFalloff) * skyRefl;
            surface += (1.0 - reflectivity) * _SurfaceColor;

            float fog = clamp(viewLen / FOG_DISTANCE + dither, 0.0, 1.0);
            surface = mix(surface, sampleFog(viewDir), fog);
            float edgeFoam = edgeFoamRaw * _EdgeFoamIntensity;
            vec3 blurredScene = sampleBlurredScene(screenUv, normal);
            surface = mix(surface, mix(blurredScene, surface, _WaterlineCompositeOpacity), _WaterBlurOpacity * (1.0 - foam));

            vec3 foamColor = _FoamLineColor;
            surface = mix(surface, foamColor, foam);
            // Edge foam paints the actual water-object contact line, regardless
            // of object shape — uses a brighten-only mix so it sits on top of
            // whatever surface tint/refraction is already there.
            surface = mix(surface, max(surface, _EdgeFoamColor), clamp(edgeFoam, 0.0, 1.0));

            // Veil the horizon band with the sky's horizon color so the ocean
            // plane edge and any grazing reflection dissolve into the sky.
            surface = mix(surface, sampleFog(viewDir), horizonHaze);

            // _SurfaceOpacity blends from physics-based alpha (Fresnel + fog) to full opacity.
            // At 0: transparent where Fresnel is low (looking straight down).
            // At 1: fully opaque regardless of view angle — makes reflection clearly visible.
            float combinedFoam = max(foam, edgeFoam);
            float physicsAlpha = max(max(reflectivity, fog), combinedFoam);
            float blurAlpha = _WaterBlurOpacity * (1.0 - foam);
            gl_FragColor = vec4(surface, max(mix(physicsAlpha, 1.0, _SurfaceOpacity), blurAlpha) * edgeFade);
            return;
        }

        float originY = cameraPosition.y;
        viewLen = min(viewLen, MAX_VIEW_DEPTH);
        float sampleY = originY + viewDir.y * viewLen;
        vec3 light = exp((sampleY - MAX_VIEW_DEPTH_DENSITY) * _Absorption);
        light *= _Light;

        float reflectivity = pow2(1.0 - max(0.0, dot(viewDir, normal)));
        float t = clamp(max(reflectivity, viewLen / MAX_VIEW_DEPTH) + dither, 0.0, 1.0);

        // Keep the original underwater surface path: the ceiling color comes
        // from absorption + scene light, which matches the underwater fog.
        // Depth-intersection foam contributes a dim refraction sheen where the
        // surface meets opaque geometry above — multiplied by both the
        // configured intensity and the underwater dim factor.
        float underwaterEdgeFoam = edgeFoamRaw * _EdgeFoamIntensity * _EdgeFoamUnderwaterMul;
        float underwaterFoam = clamp(foam * 0.3 + underwaterEdgeFoam, 0.0, 1.0);

        // Shave a touch of opacity off the surface as seen from below, but only
        // right around the camera — ease back to fully opaque as the fragment
        // approaches the horizon. t already carries that same near-far /
        // steep-grazing gradient (it drives the rest of the underwater blend),
        // so reuse it instead of a flat constant. Foam is unaffected either way.
        float underwaterOpacity = mix(0.9, 1.0, t);

        if (dot(viewDir, normal) < CRITICAL_ANGLE)
        {
            vec3 r = reflect(viewDir, -normal);
            sampleY = r.y * (MAX_VIEW_DEPTH - viewLen);
            vec3 rColor = exp((sampleY - MAX_VIEW_DEPTH_DENSITY) * _Absorption);
            rColor *= _Light;

            vec3 foamColor = _EdgeFoamColor;
            vec3 finalColor = mix(mix(rColor, light, t), foamColor, underwaterFoam);

            gl_FragColor = vec4(finalColor, max(edgeFade * underwaterOpacity, underwaterFoam));
            return;
        }

        vec3 foamColor = _EdgeFoamColor;
        vec3 finalColor = mix(light, foamColor, underwaterFoam);

        gl_FragColor = vec4(finalColor, max(t * edgeFade * underwaterOpacity, underwaterFoam));
    }
`;

export const volumeVertex = 
/*glsl*/`
    varying vec3 _worldPos;

    void main()
    {
        vec4 worldPos = modelMatrix * vec4(position, 1.0);
        _worldPos = worldPos.xyz;
        gl_Position = projectionMatrix * viewMatrix * worldPos;
    }
`;

export const volumeFragment = 
/*glsl*/`
    #include <ocean>

    varying vec3 _worldPos;

    void main()
    {
        vec3 viewVec = _worldPos - cameraPosition;
        float viewLen = length(viewVec);
        vec3 viewDir = viewVec / viewLen;
        float originY = cameraPosition.y;

        if (cameraPosition.y > 0.0)
        {
            // The volume is the underwater fog box; above water it is only valid
            // for rays travelling DOWN into the water. Near the horizon
            // (-viewDir.y -> 0) the distance-to-waterline term blows up, driving
            // viewLen hugely negative and exploding the exp() below into a
            // blown-out white speck — the underwater effect leaking above the
            // surface right at the horizon. Skip those grazing/upward rays.
            if (-viewDir.y < 0.02) discard;
            float distAbove = cameraPosition.y / -viewDir.y;
            viewLen -= distAbove;
            originY = 0.0;
        }
        viewLen = clamp(viewLen, 0.0, MAX_VIEW_DEPTH);

        float sampleY = originY + viewDir.y * viewLen;
        vec3 light = exp((sampleY - viewLen * DENSITY) * _Absorption);
        light *= _Light;

        gl_FragColor = vec4(light, 1.0);
    }
`;

export const objectVertex =
/*glsl*/`
    varying vec3 _worldPos;
    varying vec3 _normal;
    varying vec2 _uv;
    
    void main()
    {
        vec4 worldPos = modelMatrix * vec4(position, 1.0);
        _worldPos = worldPos.xyz;
        _normal = normal;
        _uv = uv;
        gl_Position = projectionMatrix * viewMatrix * worldPos;
    }
`;

export const objectFragment =
/*glsl*/`
    #include <ocean>

    uniform vec3 _CameraForward;
    uniform sampler2D _MainTexture;
    uniform float _SpotLightSharpness;
    uniform float _SpotLightDistance;

    varying vec3 _worldPos;
    varying vec3 _normal;
    varying vec2 _uv;

    void main()
    {
        float dirLighting = max(0.333, dot(_normal, _DirToLight));
        vec3 texture = texture2D(_MainTexture, _uv).xyz * dirLighting;
        
        vec3 viewVec = _worldPos - cameraPosition;
        float viewLen = length(viewVec);
        vec3 viewDir = viewVec / viewLen;

        if (_worldPos.y > 0.0)
        {
            if (cameraPosition.y < 0.0)
            {
                viewLen -= cameraPosition.y / -viewDir.y;
            }

            sampleDither(gl_FragCoord.xy);
            vec3 fogColor = sampleFog(viewDir);
            float fog = clamp(viewLen / FOG_DISTANCE + dither, 0.0, 1.0);
            gl_FragColor = vec4(mix(texture, fogColor, fog), 1.0);
            return;
        }

        float originY = cameraPosition.y;

        if (cameraPosition.y > 0.0)
        {
            viewLen -= cameraPosition.y / -viewDir.y;
            originY = 0.0;
        }
        viewLen = min(viewLen, MAX_VIEW_DEPTH);

        float sampleY = originY + viewDir.y * viewLen;
        vec3 light = exp((sampleY - viewLen * DENSITY) * _Absorption) * _Light;

        float spotLight = 0.0;
        float spotLightDistance = 1.0;
        if (_SpotLightDistance > 0.0)
        {
            spotLightDistance =  min(distance(_worldPos, cameraPosition) / _SpotLightDistance, 1.0);
            spotLight = pow(max(dot(viewDir, _CameraForward), 0.0), _SpotLightSharpness) * (1.0 - spotLightDistance);
        }
        
        light = min(light + spotLight, vec3(1.0));

        gl_FragColor = vec4(mix(texture * light, light, min(viewLen / MAX_VIEW_DEPTH, 1.0 - spotLight)), 1.0);
    }
`;

export const triplanarVertex = 
/*glsl*/`
    varying vec3 _worldPos;
    varying vec3 _normal;
    
    void main()
    {
        vec4 worldPos = modelMatrix * vec4(position, 1.0);
        _worldPos = worldPos.xyz;
        _normal = normal;
        gl_Position = projectionMatrix * viewMatrix * worldPos;
    }
`;

export const triplanarFragment =
/*glsl*/`
    #include <ocean>

    uniform vec3 _CameraForward;
    uniform sampler2D _MainTexture;
    uniform float _BlendSharpness;
    uniform float _Scale;
    uniform float _SpotLightSharpness;
    uniform float _SpotLightDistance;
    uniform float _CausticsScale;

    varying vec3 _worldPos;
    varying vec3 _normal;

    const float CAUSTIC_DISTANCE = 15.0;

    // Hash for Voronoi cell caustics
    vec2 causticHash(vec2 p) {
        p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
        return fract(sin(p) * 43758.5453);
    }

    // Voronoi-based caustic: finds distance to nearest cell edge
    // producing organic net-like patterns similar to real underwater caustics
    float voronoiCaustic(vec2 p) {
        vec2 ip = floor(p);
        vec2 fp = fract(p);

        float d1 = 8.0;  // nearest
        float d2 = 8.0;  // second nearest

        for (int y = -1; y <= 1; y++) {
            for (int x = -1; x <= 1; x++) {
                vec2 neighbor = vec2(float(x), float(y));
                vec2 point = causticHash(ip + neighbor);
                // Animate the cell centers
                point = 0.5 + 0.5 * sin(_Time * 0.8 + 6.2831 * point);
                vec2 diff = neighbor + point - fp;
                float dist = dot(diff, diff);
                if (dist < d1) {
                    d2 = d1;
                    d1 = dist;
                } else if (dist < d2) {
                    d2 = dist;
                }
            }
        }

        // Edge detection: bright where d2 ≈ d1 (cell boundaries)
        float edge = d2 - d1;
        return exp(-12.0 * edge);
    }

    float caustics(vec2 worldXZ) {
        // Uniform branch: when scale is 0 (mobile/low quality) the GPU skips
        // the entire voronoi loop, which runs 2×9 trig iterations per fragment.
        if (_CausticsScale <= 0.0) return 0.0;

        // Speed multiplier = 1.0 so drift matches the ocean wave velocities exactly
        float speed = 1.0;

        // Layer 1: drifts with wave velocity 1
        vec2 uv1 = worldXZ * 2.5 + _WaveVelocity1 * _Time * speed;
        float c1 = voronoiCaustic(uv1 * 2.0);

        // Layer 2: drifts with wave velocity 2, slightly rotated
        vec2 uv2 = worldXZ * 2.5 + _WaveVelocity2 * _Time * speed;
        float angle = 0.45;
        float ca = cos(angle);
        float sa = sin(angle);
        uv2 = vec2(uv2.x * ca - uv2.y * sa, uv2.x * sa + uv2.y * ca);
        float c2 = voronoiCaustic(uv2 * 2.0);

        // Min blend: produces the classic intersecting caustic network
        return min(c1, c2) * 3.5 * _CausticsScale;
    }

    void main()
    {
        float dirLighting = max(0.4, dot(_normal, _DirToLight));

        vec3 weights = abs(_normal);
        weights = vec3(pow(weights.x, _BlendSharpness), pow(weights.y, _BlendSharpness), pow(weights.z, _BlendSharpness));
        weights = weights / (weights.x + weights.y + weights.z);

        vec3 textureX = texture2D(_MainTexture, _worldPos.yz * _Scale).xyz * weights.x;
        vec3 textureY = texture2D(_MainTexture, _worldPos.xz * _Scale).xyz * weights.y;
        vec3 textureZ = texture2D(_MainTexture, _worldPos.xy * _Scale).xyz * weights.z;

        vec3 texture = (textureX + textureY + textureZ) * dirLighting;
        
        vec3 viewVec = _worldPos - cameraPosition;
        float viewLen = length(viewVec);
        vec3 viewDir = viewVec / viewLen;

        if (_worldPos.y > 0.0)
        {
            if (cameraPosition.y < 0.0)
            {
                viewLen -= cameraPosition.y / -viewDir.y;
            }

            sampleDither(gl_FragCoord.xy);
            vec3 fogColor = sampleFog(viewDir);
            float fog = clamp(viewLen / FOG_DISTANCE + dither, 0.0, 1.0);
            gl_FragColor = vec4(mix(texture, fogColor, fog), 1.0);
            return;
        }

        float originY = cameraPosition.y;

        if (cameraPosition.y > 0.0)
        {
            viewLen -= cameraPosition.y / -viewDir.y;
            originY = 0.0;
        }
        viewLen = min(viewLen, MAX_VIEW_DEPTH);

        float sampleY = originY + viewDir.y * viewLen;
        vec3 light = exp((sampleY - viewLen * DENSITY) * _Absorption) * _Light;

        // Caustic light on underwater surfaces
        float caustic = caustics(_worldPos.xz);
        // Depth attenuation: caustics fade on very deep surfaces.
        // Slope chosen so caustics still reach the sea floor at Y≈-14
        // (full fade at Y≈-20).
        float depthFade = clamp(1.0 + _worldPos.y * 0.05, 0.0, 1.0);
        // Distance fade: caustics only visible near the camera
        float distFade = 1.0 - clamp(viewLen / CAUSTIC_DISTANCE, 0.0, 1.0);
        distFade *= distFade; // quadratic falloff for smooth fade
        // Day: warm bright caustics | Night: cool moonlight caustics
        float dayIntensity = _SunVisibility * 0.9;
        float nightIntensity = (1.0 - _SunVisibility) * 0.6;
        vec3 causticColor = caustic * depthFade * distFade * (
            vec3(1.0, 0.95, 0.85) * dayIntensity +
            vec3(0.5, 0.65, 1.0) * nightIntensity
        );
        light += causticColor;

        float spotLight = 0.0;
        float spotLightDistance = 1.0;
        if (_SpotLightDistance > 0.0)
        {
            spotLightDistance =  min(distance(_worldPos, cameraPosition) / _SpotLightDistance, 1.0);
            spotLight = pow(max(dot(viewDir, _CameraForward), 0.0), _SpotLightSharpness) * (1.0 - spotLightDistance);
        }
        
        light = min(light + spotLight, vec3(1.0));

        gl_FragColor = vec4(mix(texture * light, light, min(viewLen / MAX_VIEW_DEPTH, 1.0 - spotLight)), 1.0);
    }
`;
